import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Send, X } from 'lucide-react';

interface QuoteRequest {
  id: string;
  email?: string;
  phone?: string | null;
  message: string | null;
  requested_quantity: number;
  status: string;
  admin_response: string | null;
  created_at: string;
  product_id: string | null;
  user_id: string | null;
  product_name?: string;
  product_description?: string;
}

interface QuoteDetailModalProps {
  quote: QuoteRequest | null;
  isOpen: boolean;
  onClose: () => void;
  onQuoteUpdated: () => void;
}

const QuoteDetailModal = ({ quote, isOpen, onClose, onQuoteUpdated }: QuoteDetailModalProps) => {
  const [adminResponse, setAdminResponse] = useState('');
  const [isResponding, setIsResponding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fullQuote, setFullQuote] = useState<QuoteRequest | null>(null);

  // Load full quote details when modal opens
  useEffect(() => {
    if (isOpen && quote?.id) {
      setFullQuote(quote);
    } else {
      setFullQuote(null);
    }
  }, [isOpen, quote]);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'ממתין';
      case 'responded':
        return 'נענה';
      case 'rejected':
        return 'נדחה';
      default:
        return status;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'responded':
        return 'default';
      case 'rejected':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const handleSendResponse = async () => {
    if (!quote || !adminResponse.trim()) {
      toast.error('אנא הזן תגובה');
      return;
    }

    setLoading(true);
    try {
      // First update the quote request in the database so status changes immediately
      const { error: updateError } = await supabase
        .from('quote_requests')
        .update({
          status: 'responded',
          admin_response: adminResponse.trim(),
        })
        .eq('id', quote.id);

      if (updateError) throw updateError;

      // Then send the email via edge function (runs with service role)
      const { data, error } = await supabase.functions.invoke('send-quote-response', {
        body: {
          quoteRequestId: quote.id,
          responseMessage: adminResponse.trim(),
          adminName: 'צוות Excess Market',
        },
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'שגיאה בשליחת המייל');
      }

      toast.success('תגובה נשלחה בהצלחה למייל הלקוח');

      setAdminResponse('');
      setIsResponding(false);
      onQuoteUpdated();
      onClose();
    } catch (error: any) {
      console.error('Error sending response:', error);
      toast.error(`שגיאה בשליחת התגובה: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  if (!quote) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" dir="rtl">
        <DialogHeader>
          <DialogTitle>פרטי בקשת הצעת מחיר</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Quote Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">אימייל</Label>
              <p className="text-sm">{fullQuote?.email || quote?.email || 'לא זמין'}</p>
            </div>
            
            {(fullQuote?.phone || quote?.phone) && (
              <div>
                <Label className="text-sm font-medium">טלפון</Label>
                <p className="text-sm">{fullQuote?.phone || quote?.phone}</p>
              </div>
            )}
            
            <div>
              <Label className="text-sm font-medium">מוצר</Label>
              <p className="text-sm">{quote?.product_name || 'מוצר לא מוגדר'}</p>
            </div>
            
            <div>
              <Label className="text-sm font-medium">כמות מבוקשת</Label>
              <p className="text-sm">{quote.requested_quantity.toLocaleString()}</p>
            </div>
            
            <div>
              <Label className="text-sm font-medium">סטטוס</Label>
              <Badge variant={getStatusBadgeVariant(quote.status)} className="w-fit">
                {getStatusText(quote.status)}
              </Badge>
            </div>
            
            <div>
              <Label className="text-sm font-medium">תאריך יצירה</Label>
              <p className="text-sm">{new Date(quote.created_at).toLocaleDateString('he-IL')}</p>
            </div>
          </div>

          {/* Customer Message */}
          {quote.message && (
            <div>
              <Label className="text-sm font-medium">הודעת הלקוח</Label>
              <div className="mt-1 p-3 bg-muted rounded-md">
                <p className="text-sm whitespace-pre-wrap">{quote.message}</p>
              </div>
            </div>
          )}

          {/* Previous Admin Response */}
          {quote.admin_response && (
            <div>
              <Label className="text-sm font-medium">תגובת המנהל</Label>
              <div className="mt-1 p-3 bg-primary/10 rounded-md">
                <p className="text-sm whitespace-pre-wrap">{quote.admin_response}</p>
              </div>
            </div>
          )}

          {/* Response Section */}
          {quote.status === 'pending' && (
            <div className="space-y-4">
              {!isResponding ? (
                <Button onClick={() => setIsResponding(true)} className="w-full">
                  <Send className="w-4 h-4 mr-2" />
                  מענה לבקשה
                </Button>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="admin-response">תגובת המנהל</Label>
                    <Textarea
                      id="admin-response"
                      value={adminResponse}
                      onChange={(e) => setAdminResponse(e.target.value)}
                      placeholder="הזן את תגובתך לבקשת ההצעה..."
                      rows={4}
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button onClick={handleSendResponse} disabled={loading} className="flex-1">
                      {loading ? 'שולח...' : 'שלח תגובה'}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setIsResponding(false);
                        setAdminResponse('');
                      }}
                      className="flex-1"
                    >
                      ביטול
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteDetailModal;